import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Building2, ChevronLeft, ChevronRight, Eye, MapPin, TrendingUp } from "lucide-react";
import { assetUrl } from "@/lib/api";

export interface TrendingListing {
  id: string;
  title: string;
  overview: string | null;
  asking_price: number | null;
  listing_image_url: string | null;
  category_name: string | null;
  city_name: string | null;
  state_name: string | null;
  country_name: string | null;
  created_at: string;
  view_count: number;
}

function formatShortPrice(p: number | null): string {
  if (p == null) return "—";
  if (p >= 1_000_000) return `$${(p / 1_000_000).toFixed(1)}M`;
  if (p >= 1_000) return `$${(p / 1_000).toFixed(0)}K`;
  return `$${p.toLocaleString()}`;
}

/* ── Hero-style full-width carousel ─────────────────────────────────── */
export function TrendingCarousel({ items }: { items: TrendingListing[] }) {
  const [current, setCurrent] = useState(0);
  const total = items.length;
  const { pathname, search } = useLocation();
  // So ListingDetail's "Back to listings" returns to the page showing the
  // carousel (dashboard/home), preserving its query string.
  const backTo = pathname + search;

  useEffect(() => {
    if (total <= 1) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(interval);
  }, [total]);

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  if (total === 0) return null;

  const item = items[current];
  const imgSrc = item.listing_image_url ? assetUrl(item.listing_image_url) : null;
  const location = [item.city_name, item.state_name, item.country_name].filter(Boolean).join(", ");

  return (
    <div className="relative">
      {/* Hero banner */}
      <Link to={`/listings/${item.id}`} state={{ from: backTo }} className="group block">
        <div className="relative h-72 w-full overflow-hidden rounded-2xl sm:h-80">
          {/* Background image */}
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={item.title}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
              <Building2 className="h-20 w-20 text-slate-400" />
            </div>
          )}

          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/40" />

          {/* Centre overlay card */}
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="w-full max-w-md rounded-2xl bg-[hsl(40_24%_7%)]/85 px-6 py-5 text-center shadow-xl ring-1 ring-primary/25 backdrop-blur-sm">
              {/* Category pill */}
              {item.category_name && (
                <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-widest text-amber-400">
                  {item.category_name}
                </p>
              )}

              {/* Title */}
              <h3 className="text-lg font-black uppercase leading-snug tracking-wide text-white sm:text-xl">
                {item.title}
              </h3>

              {/* Location */}
              {location && (
                <p className="mt-1.5 flex items-center justify-center gap-1 text-xs text-white/70">
                  <MapPin className="h-3 w-3" />
                  {location}
                </p>
              )}

              {/* Asking price button */}
              <div className="mt-3 inline-flex items-center rounded-full bg-amber-400 px-5 py-1.5 text-xs font-black uppercase tracking-widest text-amber-900 shadow">
                {formatShortPrice(item.asking_price)} Asking Price
              </div>
            </div>
          </div>

          {/* Prev / Next arrows */}
          {total > 1 && (
            <>
              <button
                onClick={(e) => { e.preventDefault(); prev(); }}
                className="absolute left-3 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm hover:bg-white/40 transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); next(); }}
                className="absolute right-3 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm hover:bg-white/40 transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </Link>

      {/* Dot indicators */}
      {total > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === current ? "w-6 bg-primary" : "w-1.5 bg-border"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Paged card carousel: three listings per slide ─────────────────── */
const CARDS_PER_PAGE = 3;

function TrendingCard({ item, rank, backTo }: { item: TrendingListing; rank: number; backTo: string }) {
  const imgSrc = item.listing_image_url ? assetUrl(item.listing_image_url) : null;
  const location = [item.city_name, item.state_name].filter(Boolean).join(", ");
  return (
    <Link
      to={`/listings/${item.id}`}
      state={{ from: backTo }}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="relative h-44 w-full overflow-hidden">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={item.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
            <Building2 className="h-12 w-12 text-slate-400" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
        <span className="absolute left-3 top-3 rounded-full bg-gold-gradient px-2.5 py-0.5 text-xs font-bold text-primary-foreground shadow">
          #{rank}
        </span>
        <span className="absolute bottom-3 right-3 rounded-full bg-amber-400 px-3 py-1 text-[0.7rem] font-black uppercase tracking-wider text-amber-900 shadow">
          {formatShortPrice(item.asking_price)}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        {item.category_name && (
          <p className="text-[0.65rem] font-bold uppercase tracking-widest text-primary">{item.category_name}</p>
        )}
        <h3 className="mt-1 line-clamp-2 font-display text-xl leading-snug text-foreground">{item.title}</h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-3 text-xs text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{location || "Location on request"}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <Eye className="h-3 w-3" />
            {item.view_count.toLocaleString()}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function TrendingCardCarousel({ items }: { items: TrendingListing[] }) {
  const pages: TrendingListing[][] = [];
  for (let i = 0; i < items.length; i += CARDS_PER_PAGE) pages.push(items.slice(i, i + CARDS_PER_PAGE));
  const total = pages.length;
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const { pathname, search } = useLocation();
  const backTo = pathname + search;

  useEffect(() => {
    if (total <= 1 || paused) return;
    const id = setTimeout(() => setCurrent((c) => (c + 1) % total), 6000);
    return () => clearTimeout(id);
  }, [current, total, paused]);

  if (total === 0) return null;
  const go = (n: number) => setCurrent(((n % total) + total) % total);

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {pages.map((page, p) => (
            <div
              key={p}
              className="grid w-full shrink-0 grid-cols-1 gap-4 sm:grid-cols-3"
              aria-hidden={p !== current}
              inert={p !== current}
            >
              {page.map((item, i) => (
                <TrendingCard key={item.id} item={item} rank={p * CARDS_PER_PAGE + i + 1} backTo={backTo} />
              ))}
            </div>
          ))}
        </div>
      </div>

      {total > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => go(current - 1)}
            aria-label="Previous trending listings"
            className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/30 text-primary transition-colors hover:bg-primary/15"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex gap-1.5">
            {pages.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show trending listings ${i * CARDS_PER_PAGE + 1}–${i * CARDS_PER_PAGE + pages[i].length}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-primary" : "w-1.5 bg-border"}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => go(current + 1)}
            aria-label="Next trending listings"
            className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/30 text-primary transition-colors hover:bg-primary/15"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Full section wrapper ─────────────────────────────────────────── */
/** Full width by default, matching PageShell — see interior-page.tsx. */
export function TrendingSection({
  items,
  fullWidth = true,
  flush = false,
}: {
  items: TrendingListing[];
  fullWidth?: boolean;
  /** Drop the page gutters — for hosts (PageShell) that already pad. */
  flush?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <section className={flush ? undefined : "px-4 pt-6 sm:px-6 lg:px-8"}>
      <div className={`${fullWidth ? "w-full" : "mx-auto max-w-6xl"} rounded-2xl border border-border/60 bg-white/80 p-5 shadow-sm`}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100">
              <TrendingUp className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight text-foreground">Trending Listings</h2>
              <p className="text-xs text-muted-foreground">Ranked by unique viewer count · Auto-scrolling</p>
            </div>
          </div>
          <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 border border-amber-200">
            {items.length} top listings
          </span>
        </div>
        <TrendingCardCarousel items={items} />
      </div>
    </section>
  );
}
